/**
 * Microphone Permission Utility
 *
 * Handles requesting and checking microphone permissions across all browsers
 * with proper error handling and user-friendly feedback.
 */

export interface MicrophonePermissionResult {
  granted: boolean;
  error?: string;
  errorType?: 'permission_denied' | 'not_found' | 'not_supported' | 'unknown';
}

/**
 * Request microphone permission with cross-browser support
 *
 * This function works with:
 * - Chrome/Edge (modern)
 * - Firefox
 * - Safari (iOS and macOS)
 * - Opera
 */
export async function requestMicrophonePermission(): Promise<MicrophonePermissionResult> {
  try {
    // Check if mediaDevices API is supported
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      return {
        granted: false,
        error: 'Your browser does not support microphone access. Please use a modern browser like Chrome, Firefox, Edge, or Safari.',
        errorType: 'not_supported',
      };
    }

    // Request microphone access
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      }
    });

    // Important: Stop all tracks immediately after permission is granted
    // We only needed permission, not to actually use the mic yet
    stream.getTracks().forEach(track => {
      track.stop();
    });

    return {
      granted: true,
    };
  } catch (error: any) {

    // Handle specific error types
    if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
      return {
        granted: false,
        error: 'Microphone access was denied. Please allow microphone access in your browser settings and try again.',
        errorType: 'permission_denied',
      };
    }

    if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
      return {
        granted: false,
        error: 'No microphone found. Please connect a microphone and try again.',
        errorType: 'not_found',
      };
    }

    if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
      return {
        granted: false,
        error: 'Microphone is already in use by another application. Please close other applications and try again.',
        errorType: 'unknown',
      };
    }

    if (error.name === 'OverconstrainedError' || error.name === 'ConstraintNotSatisfiedError') {
      return {
        granted: false,
        error: 'Microphone does not meet required constraints. Please check your microphone settings.',
        errorType: 'unknown',
      };
    }

    if (error.name === 'TypeError') {
      return {
        granted: false,
        error: 'Invalid microphone configuration. Please try again.',
        errorType: 'unknown',
      };
    }

    if (error.name === 'AbortError') {
      return {
        granted: false,
        error: 'Microphone access request was cancelled. Please try again.',
        errorType: 'unknown',
      };
    }

    // Unknown error
    return {
      granted: false,
      error: `Failed to access microphone: ${error.message || 'Unknown error'}`,
      errorType: 'unknown',
    };
  }
}

/**
 * Check current microphone permission status without requesting
 *
 * Note: Not all browsers support this API (Safari doesn't support query method)
 */
export async function checkMicrophonePermission(): Promise<'granted' | 'denied' | 'prompt' | 'unsupported'> {
  try {
    // Check if Permissions API is supported
    if (!navigator.permissions || !navigator.permissions.query) {
      return 'unsupported';
    }

    const result = await navigator.permissions.query({ name: 'microphone' as PermissionName });
    return result.state as 'granted' | 'denied' | 'prompt';
  } catch (error) {
    return 'unsupported';
  }
}

/**
 * Get user-friendly instructions for enabling microphone access
 * based on the user's browser
 */
export function getMicrophoneInstructions(): string {
  const userAgent = navigator.userAgent.toLowerCase();

  if (userAgent.includes('chrome') && !userAgent.includes('edge')) {
    return 'Click the camera icon in the address bar, then select "Always allow" for microphone access.';
  }

  if (userAgent.includes('firefox')) {
    return 'Click the microphone icon in the address bar, then select "Allow" and check "Remember this decision".';
  }

  if (userAgent.includes('safari')) {
    return 'Go to Safari > Settings > Websites > Microphone, then select "Allow" for this website.';
  }

  if (userAgent.includes('edge')) {
    return 'Click the lock icon in the address bar, then select "Allow" for microphone access.';
  }

  if (userAgent.includes('opera')) {
    return 'Click the site information icon in the address bar, then select "Allow" for microphone access.';
  }

  // Generic instructions for unknown browsers
  return 'Please allow microphone access in your browser settings to use this feature.';
}

/**
 * Check if browser is in a secure context (HTTPS or localhost)
 * Microphone access requires a secure context
 */
export function isSecureContext(): boolean {
  return window.isSecureContext || window.location.protocol === 'https:' || window.location.hostname === 'localhost';
}

/**
 * Comprehensive microphone permission request with user guidance
 * This is the main function to use in dialer components
 */
export async function requestMicrophoneWithGuidance(): Promise<MicrophonePermissionResult> {
  // Check if we're in a secure context
  if (!isSecureContext()) {
    return {
      granted: false,
      error: 'Microphone access requires HTTPS. Please use a secure connection.',
      errorType: 'not_supported',
    };
  }

  // Check current permission status first (if supported)
  const currentStatus = await checkMicrophonePermission();

  if (currentStatus === 'denied') {
    return {
      granted: false,
      error: `Microphone access was previously denied. ${getMicrophoneInstructions()}`,
      errorType: 'permission_denied',
    };
  }

  // Request permission
  const result = await requestMicrophonePermission();

  // Add browser-specific instructions if permission was denied
  if (!result.granted && result.errorType === 'permission_denied') {
    result.error = `${result.error}\n\n${getMicrophoneInstructions()}`;
  }

  return result;
}
